import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { MigrationDataAccessLayer } from '../services/migrationDataAccessLayer';
import Logger from '../services/logger';

export interface UserPermissions {
  isCompanyOwner: boolean;
  isDriver: boolean;
  canInviteDrivers: boolean;
  canModifyCompanySettings: boolean;
  canViewCompanySettings: boolean;
  canManageLoads: boolean;
  canViewPayments: boolean;
}

export const getUserPermissions = async (userId: string): Promise<UserPermissions> => {
  try {
    Logger.debug('[getUserPermissions] Fetching permissions for user:', { userId });
    const migrationDataAccess = new MigrationDataAccessLayer();
    const result = await migrationDataAccess.getUserProfile(userId);
    Logger.debug('[getUserPermissions] getUserProfile result:', { result });
    
    if (!result.success || !result.data) {
      Logger.debug('[getUserPermissions] No user data found, using default permissions');
      return getDefaultPermissions();
    }

    const userData = result.data;
    const role = userData.role || 'company_owner'; // Default to company_owner for existing users
    Logger.debug('[getUserPermissions] User role:', { role });

    const permissions = {
      isCompanyOwner: role === 'company_owner',
      isDriver: role === 'driver',
      canInviteDrivers: role === 'company_owner',
      canModifyCompanySettings: role === 'company_owner',
      canViewCompanySettings: true, // Both can view, but only owners can modify
      canManageLoads: true, // Both can manage loads
      canViewPayments: true, // Both can view payments
    };
    
    Logger.debug('[getUserPermissions] Calculated permissions:', { permissions });
    return permissions;
  } catch (error) {
    Logger.error('Error fetching user permissions:', error instanceof Error ? error : new Error(String(error)), { error });
    return getDefaultPermissions();
  }
};

const getDefaultPermissions = (): UserPermissions => ({
  isCompanyOwner: true,
  isDriver: false,
  canInviteDrivers: true,
  canModifyCompanySettings: true,
  canViewCompanySettings: true,
  canManageLoads: true,
  canViewPayments: true,
});

// Helper function to check if user is a driver
export const isDriver = (role?: string): boolean => {
  return role === 'driver';
};

// Helper function to check if user is a company owner
export const isCompanyOwner = (role?: string): boolean => {
  return role === 'company_owner';
}; 